import { useState } from "react";

import { hueForKey, identityColor } from "@/lib/identity";
import { cn } from "@/lib/utils";

// The one agent avatar, desktop and companion (docs/ui-guide `.av`):
// the agent's initial on a tint of its identity hue (--id-1 … --id-8,
// constant across themes), or its uploaded picture. Pass `hue` from
// useAgentDirectory or the agent's `template` - both give the same
// colour, since the hue is a pure function of the template. The display
// NAME never picks a colour (a rename would recolour the agent); with
// neither, the avatar is neutral grey.
//
// Presence is shown by FORM, not only colour: working = solid dot,
// active recently = hollow ring, offline = grey dot, idle = no dot. The
// label goes into the tooltip and the accessible name.
//
// Kind is shown by SHAPE: a subagent (manifest `kind: subagent`, one the
// agents spawn, never a team member) is a rounded square wherever it sits
// beside agents, which are circles. Pass `kind` from the directory entry;
// an explicit `shape` still wins. Pair it with SubagentBadge where a word
// is needed too.
//
// Sizes are the guide's fixed set; text never drops below 12px. The
// picture/initial is decorative (the name sits beside it); pass
// `ariaLabel` when the avatar stands alone.

export type AgentAvatarSize = 20 | 24 | 28 | 32 | 40 | 56 | 64;
export type AgentAvatarPresence = "working" | "recent" | "idle" | "offline" | "unknown";

export interface AgentAvatarProps {
  /** Display name; its first letter is the initial. */
  name: string;
  /** Identity hue 1…8 (useAgentDirectory's `hue`). */
  hue?: number | null;
  /** The agent's template, when `hue` is not to hand (same colour). */
  template?: string | null;
  /** Uploaded picture (already cache-busted by the API). Falls back to the initial on error. */
  src?: string | null;
  size?: AgentAvatarSize;
  /** Presence; "idle", "unknown" or null draw no dot. */
  presence?: AgentAvatarPresence | null;
  /** Presence text for the tooltip/screen readers ("Active 3 min ago"); a default per state otherwise. */
  presenceLabel?: string;
  shape?: "circle" | "rounded";
  /** With no picture: the initial (default), or a head-and-shoulders figure like a profile photo's placeholder. */
  placeholder?: "initial" | "person";
  /** The roster entry's kind; "subagent" draws the rounded square (see above). */
  kind?: string | null;
  /** Tooltip; defaults to "<name> · <presence>" when there is a presence label. */
  title?: string;
  /** Makes the avatar an image for assistive tech (otherwise only the dot is announced). */
  ariaLabel?: string;
  /**
   * Colour of the cut-out ring around the dot = the surface behind the
   * avatar. Defaults to `--avatar-ring` (set it on a card or row once),
   * else the page background.
   */
  ringColor?: string;
  className?: string;
}

const FONT_PX: Record<AgentAvatarSize, number> = { 20: 12, 24: 12, 28: 12, 32: 13, 40: 14, 56: 20, 64: 24 };
const DOT_PX: Record<AgentAvatarSize, number> = { 20: 7, 24: 8, 28: 9, 32: 10, 40: 11, 56: 13, 64: 14 };
const DEFAULT_LABEL: Record<AgentAvatarPresence, string> = {
  working: "Active now",
  recent: "Active recently",
  idle: "",
  offline: "Offline",
  unknown: "",
};
// Opaque mix (not an rgba text token) so the avatar never shows through the dot.
const QUIET = "color-mix(in srgb, var(--c-text-primary) 45%, var(--c-bg))";
const NEUTRAL = "var(--c-text-muted)";

type GraphemeSegmenter = { segment: (s: string) => Iterable<{ segment: string }> };
let segmenter: GraphemeSegmenter | null | undefined;

// First user-perceived character, upper-cased, and still ONE letter:
// "ß" upper-cases to "SS", "ǆ" to the digraph "Ǆ" (NFKC splits it);
// "É" may be E + a combining accent and stays whole.
function firstGrapheme(s: string): string {
  if (segmenter === undefined) {
    const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => GraphemeSegmenter }).Segmenter;
    segmenter = Seg ? new Seg(undefined, { granularity: "grapheme" }) : null;
  }
  if (segmenter) {
    for (const { segment } of segmenter.segment(s)) return segment;
    return "";
  }
  return /^(?:\P{M}\p{M}*)/u.exec(s)?.[0] ?? "";
}

export function initialOf(name: string): string {
  const first = firstGrapheme(name.trim());
  return first ? firstGrapheme(first.toLocaleUpperCase().normalize("NFKC")) : "?";
}

export function AgentAvatar({
  name,
  hue,
  template,
  src,
  size = 28,
  presence,
  presenceLabel,
  shape,
  placeholder = "initial",
  kind,
  title,
  ariaLabel,
  ringColor,
  className,
}: AgentAvatarProps) {
  // Remember which src failed so a replaced picture gets another chance.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && failedSrc !== src;
  const resolvedHue = hue ?? (template ? hueForKey(template) : null);
  const color = resolvedHue ? identityColor(resolvedHue) : NEUTRAL;
  const form = shape ?? (kind === "subagent" ? "rounded" : "circle");
  // About a quarter of the side: square enough to read as "not a circle" at 20px.
  const radius = form === "circle" ? "9999px" : `${Math.max(5, Math.round(size / 4.5))}px`;
  const state = presence ?? "unknown";
  const label = presenceLabel || DEFAULT_LABEL[state];
  const dot = state === "working" || state === "recent" || state === "offline";
  const dotPx = DOT_PX[size];
  const ring = ringColor ?? "var(--avatar-ring, var(--c-bg))";
  const tooltip = title ?? (label ? `${name} · ${label}` : undefined);
  const dotFill =
    state === "working" ? "var(--c-success)" : state === "offline" ? QUIET : ring; // recent: hollow
  const dotEdge = state === "recent" ? `, inset 0 0 0 ${dotPx >= 10 ? 2 : 1.5}px var(--c-success)` : "";

  return (
    <span
      className={cn("relative inline-flex shrink-0 select-none", className)}
      style={{ width: size, height: size }}
      title={tooltip}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel ? (label ? `${ariaLabel}, ${label}` : ariaLabel) : undefined}
    >
      <span
        aria-hidden
        className="flex h-full w-full items-center justify-center overflow-hidden font-semibold leading-none"
        style={{
          borderRadius: radius,
          fontSize: FONT_PX[size],
          color: `color-mix(in srgb, ${color} 55%, var(--c-text-primary))`,
          background: `color-mix(in srgb, ${color} var(--avatar-mix, 26%), transparent)`,
          boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 40%, transparent)`,
        }}
      >
        {showImage ? (
          <img
            src={src ?? undefined}
            alt=""
            width={size}
            height={size}
            draggable={false}
            onError={() => setFailedSrc(src ?? null)}
            className="block h-full w-full object-cover"
          />
        ) : placeholder === "person" ? (
          <PersonFigure />
        ) : (
          initialOf(name)
        )}
      </span>
      {dot && (
        <span
          // Announced on its own when the avatar is decorative.
          role={ariaLabel ? undefined : "img"}
          aria-label={ariaLabel ? undefined : label}
          aria-hidden={ariaLabel ? true : undefined}
          className="absolute"
          style={{
            width: dotPx,
            height: dotPx,
            right: -1,
            bottom: -1,
            borderRadius: "9999px",
            background: dotFill,
            boxShadow: `0 0 0 2px ${ring}${dotEdge}`,
          }}
        />
      )}
    </span>
  );
}

/** A profile photo's placeholder: a head and shoulders, cut by the avatar's edge, in the avatar's own colour. */
function PersonFigure() {
  return (
    <svg viewBox="0 0 64 64" className="block h-full w-full" fill="currentColor">
      <circle cx="32" cy="25" r="11.5" opacity="0.9" />
      <path d="M7 68c0-15 11-26 25-26s25 11 25 26z" opacity="0.9" />
    </svg>
  );
}
